/**
 * AdminStatusCard — self-diagnostic panel visible at the top of /app/admin
 * regardless of whether the caller is actually admin.
 *
 * Purpose: give the user a clear, in-UI answer to "am I currently admin on
 * this environment?" without ever asking them to paste a JWT or open
 * DevTools. Combines:
 *   /api/auth/me                — current identity + is_admin/role
 *   /api/auth/bootstrap-check   — WHY they are (or aren't) admin,
 *                                  what would flip them, latch state
 *
 * Both endpoints require nothing more than the already-active session
 * cookie/JWT the browser already has.
 */
import { useEffect, useState } from "react";
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, Info, Loader2, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";

// Human-readable expansion of the `why_not` reason codes returned by the
// backend. Keep in sync with routers/auth.py::bootstrap_check.
const WHY_NOT_HELP = {
  ready: {
    tone: "warn",
    title: "Bootstrap primed — one call away",
    body: "The bootstrap env vars ARE set, and your email matches the target. The next authenticated request will promote your account to super_admin. Refresh this page (or click Verify below) to trigger it.",
  },
  already_admin: {
    tone: "ok",
    title: "You already have admin access",
    body: "Your account is is_admin=true. You can use the Stripe Ops panel below.",
  },
  env_not_armed: {
    tone: "warn",
    title: "Bootstrap env vars not set on this environment",
    body: "Either ADMIN_BOOTSTRAP_ENV or ADMIN_BOOTSTRAP_EMAIL is missing on this backend. If you're on production, set them in the prod env and restart the backend. Or promote an existing admin to flip your is_admin flag from the Admin → Users list.",
  },
  email_not_verified: {
    tone: "warn",
    title: "Your email isn't verified yet",
    body: "The bootstrap flow refuses to promote unverified emails. Check your inbox for the CareerOS verification link, then refresh this page.",
  },
  target_email_mismatch: {
    tone: "info",
    title: "This account isn't the configured admin target",
    body: "The bootstrap flow is armed on this environment, but the configured target email is different from yours. Either (a) log in as the target account, or (b) have that account promote yours via Admin → Users → Make admin.",
  },
  latch_used_by_other: {
    tone: "info",
    title: "Bootstrap already used by another account",
    body: "The one-time bootstrap latch has already been consumed. Ask the current super_admin to promote your account via Admin → Users → Make admin.",
  },
  latch_used: {
    tone: "info",
    title: "Bootstrap already consumed",
    body: "The one-time bootstrap latch has already been triggered. Existing admins can promote you via Admin → Users.",
  },
};

function tile(tone) {
  return tone === "ok"
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
    : tone === "warn"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
      : tone === "bad"
        ? "border-red-500/40 bg-red-500/10 text-red-100"
        : "border-border/60 bg-muted/40";
}

export default function AdminStatusCard() {
  const [me, setMe] = useState(null);
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [oneShot, setOneShot] = useState(null); // {available, reason}
  const [claiming, setClaiming] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      // Three calls in parallel — all use the existing browser session.
      // The one-shot status is public (unauthenticated) so it never leaks
      // the target email even in the network tab.
      const [meR, chkR, osR] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/bootstrap-check").catch((e) => ({ __err: e })),
        api.get("/auth/one-shot/claim-super-admin/status").catch(() => ({ data: null })),
      ]);
      setMe(meR.data || null);
      if (chkR && chkR.__err) {
        setCheck({ __error: errorMessage(chkR.__err, "bootstrap-check failed") });
      } else {
        setCheck(chkR.data || null);
      }
      setOneShot(osR?.data || null);
    } catch (e) {
      setErr(errorMessage(e, "Couldn't load account status"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/auth/one-shot/claim-super-admin", {});
      // On success, reload the whole card so the badges + Stripe Ops panel
      // pick up the new is_admin/role. Toast the outcome.
      const wasNoOp = !!data?.was_already_admin;
      const { toast } = await import("sonner");
      toast.success(
        wasNoOp
          ? "You were already super-admin — mechanism now disabled."
          : "Promoted to super-admin. Mechanism disabled."
      );
      // Small delay so the toast is visible before the page shifts.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(errorMessage(e, "Claim failed."));
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div
        className="card-flat p-5 mb-6 flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="admin-status-card-loading"
      >
        <Loader2 size={14} className="animate-spin" /> Checking your admin status…
      </div>
    );
  }

  if (err) {
    return (
      <div className="card-flat p-5 mb-6 text-sm text-red-500" data-testid="admin-status-card-error">
        {err}
      </div>
    );
  }

  const isAdmin = !!me?.is_admin;
  const role = me?.role || (isAdmin ? "admin" : "user");
  const whyNot = check?.why_not;
  const help = whyNot ? WHY_NOT_HELP[whyNot] : null;
  const headlineTone = isAdmin ? "ok" : help?.tone || "info";
  const Icon = isAdmin
    ? ShieldCheck
    : headlineTone === "warn"
      ? ShieldAlert
      : ShieldQuestion;

  return (
    <div
      className={`card-flat p-5 mb-6 border rounded-2xl ${tile(headlineTone)}`}
      data-testid="admin-status-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Icon size={20} className="mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">
              Account status
            </div>
            <div className="font-display font-bold text-lg mt-0.5" data-testid="admin-status-headline">
              {isAdmin
                ? `Signed in as admin — ${me?.email}`
                : `Signed in as ${me?.email} — not currently admin`}
            </div>
          </div>
        </div>
        <button
          type="button"
          data-testid="admin-status-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 text-xs opacity-80 hover:opacity-100 transition-opacity"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Email" value={me?.email || "—"} testId="admin-status-email" />
        <Stat
          label="is_admin"
          value={isAdmin ? "true" : "false"}
          tone={isAdmin ? "ok" : "warn"}
          testId="admin-status-is-admin"
        />
        <Stat label="role" value={role} testId="admin-status-role" />
        <Stat
          label="email_verified"
          value={me?.email_verified ? "true" : "false"}
          tone={me?.email_verified ? "ok" : "warn"}
          testId="admin-status-verified"
        />
      </div>

      {/* Bootstrap diagnostic panel — only shown when useful */}
      {check?.__error && (
        <div className="mt-4 text-xs text-red-300" data-testid="admin-status-bootstrap-error">
          Bootstrap diagnostic failed: <code>{check.__error}</code>
        </div>
      )}
      {check && !check.__error && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-2">
            Bootstrap diagnostic
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="admin-status-bootstrap-grid">
            <BoolPill label="env_armed" v={check.env_armed} />
            <BoolPill label="latch_used" v={check.latch_used} tone="info" />
            <BoolPill label="latch_owned_by_this_user" v={check.latch_owned_by_this_user} tone="info" />
            <BoolPill label="target_matches_caller" v={check.target_matches_caller} tone="info" />
            <BoolPill label="would_promote_next_call" v={check.would_promote_next_call} tone={check.would_promote_next_call ? "ok" : "info"} />
            <BoolPill label="currently_is_admin" v={check.currently_is_admin} tone={check.currently_is_admin ? "ok" : "warn"} />
          </div>
          {help && (
            <div className="mt-4 text-sm space-y-1" data-testid="admin-status-help">
              <div className="font-semibold flex items-center gap-1.5">
                <Info size={13} /> {help.title}
              </div>
              <div className="opacity-90">{help.body}</div>
              <div className="text-[10px] font-mono opacity-60 pt-1">
                why_not = {whyNot}
              </div>
            </div>
          )}
        </div>
      )}

      {/*
        One-shot super-admin claim — visible ONLY when:
          * The endpoint's public status probe reports `available: true`
            (i.e. running on prod + latch not yet consumed)
          * The caller is signed-in, email_verified, and NOT already admin.
        The button is deliberately non-committal in its label so no user
        who isn't the pre-configured target sees anything actionable —
        clicking it as a non-target yields a plain "Not eligible" toast
        with zero information leak.
      */}
      {!isAdmin && oneShot?.available === true && me?.email_verified && (
        <div
          className="mt-4 border-t border-white/10 pt-4"
          data-testid="admin-status-oneshot"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-2">
            One-time super-admin claim
          </div>
          <div className="text-sm opacity-90 mb-3">
            This environment has a pre-configured production super-admin claim
            armed for a single specific account. If that&apos;s you, click
            below to complete the bootstrap. Otherwise the button will
            safely report &quot;Not eligible.&quot;
          </div>
          <button
            type="button"
            data-testid="admin-status-oneshot-claim"
            onClick={claim}
            disabled={claiming}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40 text-sm font-medium transition-colors"
          >
            {claiming ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {claiming ? "Claiming…" : "Claim super-admin (one-time)"}
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const t = tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-amber-200" : "";
  return (
    <div className="rounded-md border border-white/10 bg-black/20 p-2.5">
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">
        {label}
      </div>
      <div
        className={`font-mono text-sm font-semibold break-all ${t}`}
        data-testid={testId}
      >
        {String(value)}
      </div>
    </div>
  );
}

function BoolPill({ label, v, tone }) {
  const on = v === true;
  const badge = on
    ? tone === "info"
      ? "bg-sky-500/20 text-sky-200 border-sky-500/40"
      : "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
    : "bg-white/5 text-white/60 border-white/10";
  return (
    <div className="flex items-center justify-between gap-2 border border-white/10 bg-black/20 rounded-md px-2.5 py-1.5">
      <span className="font-mono text-[10px] opacity-80 truncate">{label}</span>
      <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${badge}`}>
        {on ? "true" : "false"}
      </span>
    </div>
  );
}
